// MUITiptapWrapper.js
import React, { useEffect } from "react";
import { Box, Button, FormControl, MenuItem, Select } from "@mui/material";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextStyle from "@tiptap/extension-text-style";
import FontSize from "./FontSize"; // adjust path as needed

import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { Tooltip } from "@mui/material";

// Material UI Icons
import BoldIcon from "@mui/icons-material/FormatBold";
import ItalicIcon from "@mui/icons-material/FormatItalic";
import UnderlineIcon from "@mui/icons-material/FormatUnderlined";
import LinkIcon from "@mui/icons-material/Link";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import CodeIcon from "@mui/icons-material/Code";
import ListIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import AlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import AlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import AlignRightIcon from "@mui/icons-material/FormatAlignRight";
import ClearIcon from "@mui/icons-material/Clear";

// Editor Toolbar Component
function EditorToolbar({ editor }) {
  if (!editor) return null;

  const addLink = () => {
    const url = prompt("Enter URL");
    if (url) {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    }
  };

  // Define custom styling for smaller buttons
  const buttonStyle = {
    minWidth: "auto",
    padding: "4px",
  };

  return (
    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
      <Tooltip title="Text Size" placement="top">
        <FormControl
          size="small"
          sx={{
            minWidth: 80,
            "& .MuiOutlinedInput-root": {
              height: "32px",
              paddingRight: "8px",
              color: (theme) => theme.palette.primary.main,
              borderColor: (theme) => theme.palette.primary.main,
              "& fieldset": {
                borderColor: (theme) => theme.palette.primary.main,
              },
              "&:hover fieldset": {
                borderColor: (theme) => theme.palette.primary.dark,
              },
              "&.Mui-focused fieldset": {
                borderColor: (theme) => theme.palette.primary.main,
              },
            },
          }}
        >
          <Select
            value={editor.getAttributes("textStyle").fontSize || "16px"}
            displayEmpty
            onChange={(e) => {
              const size = e.target.value;
              editor.chain().focus().setFontSize(size).run();
            }}
          >
            <MenuItem value="12px">12</MenuItem>
            <MenuItem value="14px">14</MenuItem>
            <MenuItem value="16px">16</MenuItem>
            <MenuItem value="18px">18</MenuItem>
            <MenuItem value="24px">24</MenuItem>
            <MenuItem value="32px">32</MenuItem>
            <MenuItem value="40px">40</MenuItem>
          </Select>
        </FormControl>
      </Tooltip>

      <Tooltip placement="top" title="Heading">
        <FormControl
          size="small"
          sx={{
            minWidth: 80,
            "& .MuiOutlinedInput-root": {
              height: "32px",
              paddingRight: "8px",
              color: (theme) => theme.palette.primary.main,
              borderColor: (theme) => theme.palette.primary.main,
              "& fieldset": {
                borderColor: (theme) => theme.palette.primary.main,
              },
              "&:hover fieldset": {
                borderColor: (theme) => theme.palette.primary.dark,
              },
              "&.Mui-focused fieldset": {
                borderColor: (theme) => theme.palette.primary.main,
              },
            },
          }}
        >
          <Select
            labelId="heading-select-label"
            value={
              editor.isActive("heading", { level: 1 })
                ? "h1"
                : editor.isActive("heading", { level: 2 })
                ? "h2"
                : editor.isActive("heading", { level: 3 })
                ? "h3"
                : editor.isActive("heading", { level: 4 })
                ? "h4"
                : editor.isActive("heading", { level: 5 })
                ? "h5"
                : editor.isActive("heading", { level: 6 })
                ? "h6"
                : "paragraph"
            }
            onChange={(e) => {
              const value = e.target.value;
              const chain = editor.chain().focus();

              if (value === "paragraph") {
                chain.setParagraph().run();
              } else {
                const level = parseInt(value.replace("h", ""), 10);
                chain.toggleHeading({ level }).run();
              }
            }}
          >
            <MenuItem value="paragraph">Paragraph</MenuItem>
            <MenuItem
              value="h1"
              sx={{
                fontSize: "40px",
              }}
            >
              Heading 1
            </MenuItem>
            <MenuItem
              value="h2"
              sx={{
                fontSize: "32px",
              }}
            >
              Heading 2
            </MenuItem>
            <MenuItem
              value="h3"
              sx={{
                fontSize: "28px",
              }}
            >
              Heading 3
            </MenuItem>
            <MenuItem
              value="h4"
              sx={{
                fontSize: "22px",
              }}
            >
              Heading 4
            </MenuItem>
            <MenuItem
              value="h5"
              sx={{
                fontSize: "18px",
              }}
            >
              Heading 5
            </MenuItem>
            <MenuItem
              value="h6"
              sx={{
                fontSize: "16px",
              }}
            >
              Heading 6
            </MenuItem>
          </Select>
        </FormControl>
      </Tooltip>

      <Tooltip placement="top" title="Bold">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().toggleBold().run()}
          variant={editor.isActive("bold") ? "contained" : "outlined"}
        >
          <BoldIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Italic">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          variant={editor.isActive("italic") ? "contained" : "outlined"}
        >
          <ItalicIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Underline">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          variant={editor.isActive("underline") ? "contained" : "outlined"}
        >
          <UnderlineIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Link">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={addLink}
          variant={editor.isActive("link") ? "contained" : "outlined"}
        >
          <LinkIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Blockquote">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          variant={editor.isActive("blockquote") ? "contained" : "outlined"}
        >
          <FormatQuoteIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Code Block">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          variant={editor.isActive("codeBlock") ? "contained" : "outlined"}
        >
          <CodeIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Bullet List">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          variant={editor.isActive("bulletList") ? "contained" : "outlined"}
        >
          <ListIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Numbered List">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          variant={editor.isActive("orderedList") ? "contained" : "outlined"}
        >
          <FormatListNumberedIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Align Left">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          variant={
            editor.isActive({ textAlign: "left" }) ? "contained" : "outlined"
          }
        >
          <AlignLeftIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Align Center">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          variant={
            editor.isActive({ textAlign: "center" }) ? "contained" : "outlined"
          }
        >
          <AlignCenterIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Align Right">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          variant={
            editor.isActive({ textAlign: "right" }) ? "contained" : "outlined"
          }
        >
          <AlignRightIcon />
        </Button>
      </Tooltip>
      <Tooltip placement="top" title="Clear Formatting">
        <Button
          size="small"
          sx={buttonStyle}
          onClick={() =>
            editor
              .chain()
              .focus()
              .unsetAllMarks()
              .clearNodes()
              .setFontSize(null)
              .run()
          }
          variant="outlined"
        >
          <ClearIcon />
        </Button>
      </Tooltip>
    </Box>
  );
}

// MUITiptapWrapper Component
function MUITiptapWrapper({ value, onChange, placeholder }) {
  const editor = useEditor({
    content: value || "",
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      FontSize,
    ],
    editorProps: {
      attributes: {
        placeholder: placeholder,
      },
    },
    onUpdate: ({ editor }) => {
      // Return updated HTML content on every change
      onChange(editor.getHTML());
    },
  });

  // Update editor content if the value prop changes externally.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <Box border="1px solid #ccc" padding={2} borderRadius={1}>
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} />
    </Box>
  );
}

export default MUITiptapWrapper;
